import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import ImageCropper from '../components/ImageCropper';
import { User } from 'lucide-react';
import { toast } from 'sonner';

const ProfileEditPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    display_name: '',
    bio: '',
    location: '',
    website_url: '',
    twitter_handle: '',
    github_handle: '',
    linkedin_url: '',
    substack_url: '',
    reddit_url: '',
    avatar_url: '',
    cover_photo_url: '',
  });
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropType, setCropType] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        display_name: user.display_name || '',
        bio: user.bio || '',
        location: user.location || '',
        website_url: user.website_url || '',
        twitter_handle: user.twitter_handle || '',
        github_handle: user.github_handle || '',
        linkedin_url: user.linkedin_url || '',
        substack_url: user.substack_url || '',
        reddit_url: user.reddit_url || '',
        avatar_url: user.avatar_url || '',
        cover_photo_url: user.cover_photo_url || '',
      });
    }
  }, [user]);

  // Check username availability when it changes
  useEffect(() => {
    if (formData.username && formData.username !== user?.username) {
      const checkUsername = async () => {
        setCheckingUsername(true);
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/check-username/${formData.username}`);
          const data = await response.json();
          setUsernameAvailable(data.available);
        } catch (error) {
          console.error('Error checking username:', error);
        } finally {
          setCheckingUsername(false);
        }
      };
      const debounce = setTimeout(checkUsername, 500);
      return () => clearTimeout(debounce);
    } else {
      setUsernameAvailable(true);
    }
  }, [formData.username, user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await usersApi.update(formData);
      toast.success('Profile updated successfully!');
      window.location.reload(); // Refresh to update auth context
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="settings-page">
      <h1 className="text-4xl font-manrope font-bold text-[#3A1E10] mb-8" data-testid="settings-title">
        Profile Settings
      </h1>

      <Card>
        <CardHeader>
          <CardTitle className="font-manrope">Edit Profile</CardTitle>
          <CardDescription>Update your public profile information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username">Username (Handle) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="yourusername"
                  className="pl-8"
                  required
                  data-testid="username-input"
                />
                {checkingUsername && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    Checking...
                  </span>
                )}
                {!checkingUsername && formData.username !== user?.username && (
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${usernameAvailable ? 'text-green-500' : 'text-red-500'}`}>
                    {usernameAvailable ? '✓ Available' : '✗ Taken'}
                  </span>
                )}
              </div>
              {!usernameAvailable && (
                <p className="text-xs text-red-500">This username is already taken</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                placeholder="Your display name"
                data-testid="display-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="avatar">Avatar Image</Label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary-dark/20 flex items-center justify-center overflow-hidden">
                  {formData.avatar_url ? (
                    <img src={formData.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl font-bold text-primary">
                      {user?.username?.[0]?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    id="avatar"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = () => {
                          setCropImage(reader.result);
                          setCropType('avatar');
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <label htmlFor="avatar" className="cursor-pointer px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors inline-block">
                    Upload Avatar
                  </label>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cover">Cover Photo</Label>
              <div className="space-y-2">
                <div className="w-full h-32 rounded-lg bg-gradient-to-r from-primary/20 via-primary/30 to-primary-dark/20 flex items-center justify-center overflow-hidden">
                  {formData.cover_photo_url ? (
                    <img src={formData.cover_photo_url} alt="Cover" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-gray-400">No cover photo</span>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    id="cover"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = () => {
                          setCropImage(reader.result);
                          setCropType('cover');
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <label htmlFor="cover" className="cursor-pointer px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors inline-block">
                    Upload Cover Photo
                  </label>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB, recommended 1600x400px</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell us about yourself (max 280 characters)"
                maxLength={280}
                rows={4}
                data-testid="bio-input"
              />
              <p className="text-sm text-gray-500">{formData.bio.length}/280 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="e.g., San Francisco, CA"
                data-testid="location-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website_url">Website</Label>
              <Input
                id="website_url"
                name="website_url"
                type="url"
                value={formData.website_url}
                onChange={handleChange}
                placeholder="https://yourwebsite.com"
                data-testid="website-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="twitter_handle">Twitter Handle</Label>
                <Input
                  id="twitter_handle"
                  name="twitter_handle"
                  value={formData.twitter_handle}
                  onChange={handleChange}
                  placeholder="username (without @)"
                  data-testid="twitter-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="github_handle">GitHub Handle</Label>
                <Input
                  id="github_handle"
                  name="github_handle"
                  value={formData.github_handle}
                  onChange={handleChange}
                  placeholder="username"
                  data-testid="github-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="linkedin_url">LinkedIn URL</Label>
              <Input
                id="linkedin_url"
                name="linkedin_url"
                type="url"
                value={formData.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/username"
                data-testid="linkedin-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="substack_url">Substack URL</Label>
              <Input
                id="substack_url"
                name="substack_url"
                type="url"
                value={formData.substack_url}
                onChange={handleChange}
                placeholder="https://username.substack.com"
                data-testid="substack-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reddit_url">Reddit Profile URL</Label>
              <Input
                id="reddit_url"
                name="reddit_url"
                type="url"
                value={formData.reddit_url}
                onChange={handleChange}
                placeholder="https://reddit.com/u/username"
                data-testid="reddit-input"
              />
            </div>

            <div className="flex justify-between items-center">
              {!usernameAvailable && (
                <p className="text-sm text-red-500">Please choose an available username</p>
              )}
              <div className="flex gap-3 ml-auto">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/profile/${user?.id}`)}
                  className="rounded-xl"
                  data-testid="view-profile-button"
                >
                  <User className="mr-2 h-4 w-4" />
                  View Profile
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving || !usernameAvailable} 
                  className="gradient-primary text-white rounded-xl" 
                  data-testid="save-profile-button"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Image Cropper Modal */}
      {cropImage && (
        <ImageCropper
          image={cropImage}
          aspectRatio={cropType === 'avatar' ? 1 : 16 / 4}
          title={cropType === 'avatar' ? 'Crop Avatar' : 'Crop Cover Photo'}
          onCropComplete={async (croppedBlob) => {
            const formData = new FormData();
            formData.append('file', croppedBlob, 'image.jpg');
            formData.append('image_type', cropType);
            
            try {
              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/upload-image`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: formData
              });
              const data = await response.json();
              
              if (cropType === 'avatar') {
                setFormData(prev => ({ ...prev, avatar_url: data.url }));
                toast.success('Avatar uploaded!');
              } else {
                setFormData(prev => ({ ...prev, cover_photo_url: data.url }));
                toast.success('Cover photo uploaded!');
              }
            } catch (error) {
              toast.error('Failed to upload image');
            } finally {
              setCropImage(null);
              setCropType(null);
            }
          }}
          onCancel={() => {
            setCropImage(null);
            setCropType(null);
          }}
        />
      )}
    </div>
  );
};

export default ProfileEditPage;